import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  Container,
  Alert,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { TopBar } from '../layout/TopBar';
import { BottomNavigation } from '../layout/BottomNavigation';
import { useNavigate } from 'react-router-dom';
import { createPrivacyRequest } from '../services/privacyRequestService';

export const PrivacyRequestPage: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [requestType, setRequestType] = useState<'delete_account' | 'export_data'>('delete_account');
  const [reason, setReason] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user && !email) {
      setError('Por favor, informe seu email para que possamos processar a solicitação.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      await createPrivacyRequest(
        user ? user.uid : null,
        user ? (user.displayName || 'Usuário') : (name || 'Visitante'),
        user ? (user.email || '') : email,
        requestType,
        reason.trim()
      );

      setSuccess(true);
      setReason('');
      setEmail('');
      setName('');

      setTimeout(() => {
        if (user) {
          navigate('/settings');
        } else {
          navigate('/');
        }
      }, 3000);
    } catch (err) {
      console.error('Erro ao enviar solicitação:', err);
      setError('Erro ao enviar solicitação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TopBar
        title="Privacidade de Dados"
        icon={<SecurityIcon sx={{ fontSize: '0.875rem' }} />}
        showBackButton
        onBackClick={() => navigate(user ? '/settings' : '/')}
      />
      <Box
        sx={{
          backgroundColor: theme.colors.background,
          minHeight: '100vh',
          paddingTop: '70px',
          paddingBottom: user ? '120px' : '40px',
        }}
      >
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Card
            sx={{
              p: 3,
              backgroundColor: theme.colors.card,
              borderRadius: theme.radii.large,
              border: `1px solid ${theme.colors.border}`,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: theme.colors.text.primary,
                mb: 1,
              }}
            >
              Solicitação de Privacidade
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.colors.text.secondary,
                mb: 3,
              }}
            >
              Você pode solicitar a exclusão de seus dados ou exportar suas informações.
            </Typography>

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Solicitação recebida! Para sua segurança, enviaremos um e-mail de confirmação para verificar sua identidade antes de processar a exclusão ou exportação dos dados.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                
                {!user && (
                  <>
                    <TextField
                      label="Seu Nome"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      fullWidth
                      required
                      disabled={loading || success}
                    />
                    <TextField
                      label="Seu Email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      required
                      disabled={loading || success}
                      helperText="Usaremos este email para confirmar sua identidade e responder à solicitação."
                    />
                  </>
                )}

                <FormControl component="fieldset" disabled={loading || success}>
                  <FormLabel
                    component="legend"
                    sx={{
                      color: theme.colors.text.primary,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    Tipo de solicitação
                  </FormLabel>
                  <RadioGroup
                    value={requestType}
                    onChange={(e) => setRequestType(e.target.value as 'delete_account' | 'export_data')}
                  >
                    <FormControlLabel
                      value="delete_account"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            Exclusão de conta e dados
                          </Typography>
                          <Typography variant="caption" color={theme.colors.text.secondary}>
                            Todos os seus dados serão permanentemente deletados
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="export_data"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            Exportação de dados
                          </Typography>
                          <Typography variant="caption" color={theme.colors.text.secondary}>
                            Receba uma cópia de todos os seus dados
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                <TextField
                  label="Motivo (opcional)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  disabled={loading || success}
                  placeholder="Conte-nos o motivo da sua solicitação..."
                />

                {requestType === 'delete_account' && (
                  <Alert severity="warning">
                    <Typography variant="body2">
                      <strong>Atenção:</strong> A exclusão da conta é irreversível. Todos os seus dados, eventos, acervo e configurações serão permanentemente deletados.
                    </Typography>
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || success}
                  startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  sx={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.text.contrast,
                    borderRadius: theme.radii.medium,
                    padding: '12px',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: theme.colors.primary,
                      opacity: 0.9,
                    },
                    '&:disabled': {
                      backgroundColor: theme.colors.primary,
                      opacity: 0.5,
                    },
                  }}
                >
                  {loading ? 'Enviando...' : 'Enviar solicitação'}
                </Button>

                <Typography
                  variant="caption"
                  sx={{
                    color: theme.colors.text.secondary,
                    textAlign: 'center',
                  }}
                >
                  Sua solicitação será processada em até 30 dias conforme a LGPD
                </Typography>
              </Box>
            </form>
          </Card>
        </Container>
      </Box>
      {user && <BottomNavigation />}
    </>
  );
};